import { ARENA } from '../../../config/constants.js';

/**
 * Pairwise circle separation so minions, the boss, and the player don't
 * stack on top of each other — run once per frame after every entity has
 * already moved for that frame (see CombatSystem.update, called last in
 * Game.js's update order, after Player/MinionWaveSystem/Boss have all
 * moved). Resolution is asymmetric by design rather than a real physics
 * push: the player is never displaced by this pass — an external nudge
 * would fight the precise WASD/dash control the game is built around (see
 * Player.js) — so anything overlapping the player gets pushed off of it
 * instead. The boss is likewise never displaced by minions, since its
 * position is driven by its own attack patterns (see Boss.js's `locked`
 * flag); it does yield to the player, same reasoning as minions do.
 */
export function resolveOverlaps(minions, boss, player) {
  _separateMinions(minions);

  if (boss) {
    for (const minion of minions) {
      if (_separate(minion, boss, 1, 0)) _clampToArena(minion);
    }
  }

  for (const minion of minions) {
    if (_separate(minion, player, 1, 0)) _clampToArena(minion);
  }

  if (boss) _separate(boss, player, 1, 0);
}

// Below this count, the naive all-pairs pass is cheaper than building a grid,
// and it is what every boss stage and most of the gauntlet actually runs.
const GRID_THRESHOLD = 48;

/**
 * Minion-vs-minion separation.
 *
 * All-pairs is O(n^2), which was free at the wave stage's old peak of 8-14
 * (about 90 pairs a frame) and is not at the crowd pass's peaks — 40 on a wave
 * stage is 780 pairs, and the Undersong Tide's 140 (plus Swarmling clusters
 * and Splitter children on top) is upwards of 12,000 every frame, on hardware
 * that includes a Raspberry Pi.
 *
 * Above GRID_THRESHOLD this bins entities into a uniform grid whose cell is
 * sized to twice the largest radius present, so two overlapping bodies — whose
 * centers are by definition within the sum of their radii — always land in the
 * same cell or in touching ones, and checking each cell against its 3x3
 * neighborhood finds every genuinely overlapping pair. Each pair is visited
 * once (neighbors are only scanned forward), so the resolution is identical to
 * the naive pass; only the pairs that could never have touched are skipped.
 */
function _separateMinions(minions) {
  const n = minions.length;
  if (n < 2) return;

  if (n < GRID_THRESHOLD) {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (_separate(minions[i], minions[j], 0.5, 0.5)) {
          _clampToArena(minions[i]);
          _clampToArena(minions[j]);
        }
      }
    }
    return;
  }

  let maxRadius = 0;
  for (const m of minions) {
    if (Number.isFinite(m.radius) && m.radius > maxRadius) maxRadius = m.radius;
  }
  // Degenerate input (every entity radius-less) — _separate would reject all of
  // them anyway, and a zero cell size would divide by zero.
  if (maxRadius <= 0) return;
  const cell = maxRadius * 2;

  /** @type {Map<number, number[]>} cell key -> indices into `minions` */
  const grid = new Map();
  // Key stride. +3 absorbs the one-cell offset below plus any entity nudged
  // just outside the arena, so a row can never bleed into the next one's keys.
  const cols = Math.ceil(ARENA.width / cell) + 3;
  const keyOf = (cx, cy) => cy * cols + cx;
  // +1 so a body sitting exactly on the arena's left/top edge lands in column
  // 1 rather than 0, leaving room for the -1 neighbor lookups.
  const colOf = (x) => Math.floor((x - ARENA.x) / cell) + 1;
  const rowOf = (y) => Math.floor((y - ARENA.y) / cell) + 1;

  for (let i = 0; i < n; i++) {
    const m = minions[i];
    if (!Number.isFinite(m.radius)) continue;
    const key = keyOf(colOf(m.x), rowOf(m.y));
    const bucket = grid.get(key);
    if (bucket) bucket.push(i);
    else grid.set(key, [i]);
  }

  // Forward-only neighborhood: the four cells after the current one in row-
  // major order, plus the cell itself. Together with visiting every occupied
  // cell, this covers each of the nine adjacencies exactly once.
  const FORWARD = [[1, 0], [-1, 1], [0, 1], [1, 1]];

  for (const [key, bucket] of grid) {
    for (let a = 0; a < bucket.length; a++) {
      const ia = bucket[a];
      for (let b = a + 1; b < bucket.length; b++) {
        const ib = bucket[b];
        if (_separate(minions[ia], minions[ib], 0.5, 0.5)) {
          _clampToArena(minions[ia]);
          _clampToArena(minions[ib]);
        }
      }
    }
    const cy = Math.floor(key / cols);
    const cx = key - cy * cols;
    for (const [dx, dy] of FORWARD) {
      const other = grid.get(keyOf(cx + dx, cy + dy));
      if (!other) continue;
      for (const ia of bucket) {
        for (const ib of other) {
          if (_separate(minions[ia], minions[ib], 0.5, 0.5)) {
            _clampToArena(minions[ia]);
            _clampToArena(minions[ib]);
          }
        }
      }
    }
  }
}

/** Pushes `a` by `shareA` and `b` by `shareB` (0-1) of the overlap along their connecting axis. Returns whether they actually overlapped. */
function _separate(a, b, shareA, shareB) {
  // Guards against test/mock entities that omit radius (real Player/Boss/
  // minion instances always set it in their constructors) — without this,
  // minDist goes NaN, `dist >= minDist` is always false, and every pair
  // looks "overlapping" forever, corrupting positions to NaN.
  if (!Number.isFinite(a.radius) || !Number.isFinite(b.radius)) return false;

  const dx = a.x - b.x;
  const dy = a.y - b.y;
  let dist = Math.hypot(dx, dy);
  const minDist = a.radius + b.radius;
  if (dist >= minDist) return false;

  // Fully coincident (dist === 0, e.g. two minions unlucky enough to spawn
  // on the same point): direction is undefined, so pick an arbitrary one
  // rather than divide by zero and leave them permanently stuck together.
  const nx = dist === 0 ? 1 : dx / dist;
  const ny = dist === 0 ? 0 : dy / dist;
  if (dist === 0) dist = 0.0001;

  const overlap = minDist - dist;
  a.x += nx * overlap * shareA;
  a.y += ny * overlap * shareA;
  b.x -= nx * overlap * shareB;
  b.y -= ny * overlap * shareB;
  return true;
}

// Same bounds clamp as Skirmisher/Charger/Bomber's private _clampToArena —
// a minion nudged apart from a neighbor mid-frame needs the same
// defense-in-depth so it can't be shoved outside the fightable area.
function _clampToArena(entity) {
  entity.x = Math.min(ARENA.x + ARENA.width - entity.radius, Math.max(ARENA.x + entity.radius, entity.x));
  entity.y = Math.min(ARENA.y + ARENA.height - entity.radius, Math.max(ARENA.y + entity.radius, entity.y));
}
